/**
 * Suggest an idea, report a problem or ask a question from anywhere (§10 K10, L1). The page, the event, the app
 * version and the browser are captured automatically — the person is never asked for them — and who sent it
 * is set by the database. Submitting works whether or not steering edits are switched on (L6).
 * The header shows it as "Suggest idea" (owner, 2026-10-13: "add suggest idea button that will send to my manager
 * dashboard"); what is sent is stored in product_ideas, and the box forwarder tells the manager its id (0125).
 */
import { useState } from 'react';
import { useMatch, useLocation } from 'react-router-dom';
import { Lightbulb } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { writeInsert } from '@/lib/write';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { SelectField, TextArea, TextField } from '@/components/ui/Field';
import { useToast } from '@/components/ui/Toast';

const KINDS = [
  { id: 'idea', label: 'Suggest an idea', title: 'What would help?' },
  { id: 'bug', label: 'Report a problem', title: 'What went wrong?' },
  { id: 'question', label: 'Ask a question', title: 'Your question' },
] as const;

export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="inline-flex h-9 min-w-9 shrink-0 items-center justify-center gap-1.5 rounded-control px-2 text-sm font-medium text-ink-2 hover:bg-surface-2 hover:text-ink" aria-label="Suggest idea" title="Suggest idea" onClick={() => setOpen(true)} data-testid="feedback-button">
        <Lightbulb className="size-4 shrink-0" aria-hidden />
        <span className="hidden whitespace-nowrap lg:inline" aria-hidden>Suggest idea</span>
      </button>
      <FeedbackDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function FeedbackDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const loc = useLocation();
  const eventMatch = useMatch('/events/:eventId/*');
  const [f, setF] = useState<{ kind: (typeof KINDS)[number]['id']; title: string; body: string }>({ kind: 'idea', title: '', body: '' });
  const [busy, setBusy] = useState(false);
  const kind = KINDS.find((k) => k.id === f.kind)!;
  const context = { path: loc.pathname, event_id: eventMatch?.params.eventId ?? null, version: __BUILD_INFO__.version, build: __BUILD_INFO__.sha, browser: navigator.userAgent, viewport: `${window.innerWidth}×${window.innerHeight}`, at: new Date().toISOString() };
  const send = async () => {
    setBusy(true);
    const r = await writeInsert(supabase, 'product_ideas', { kind: f.kind, title: f.title.trim(), body: f.body.trim() || null, category: f.kind === 'bug' ? 'bug' : null, context });
    setBusy(false);
    if (!r.ok) { toast.error(r.message, 'write-not-saved'); return; }
    toast.success(f.kind === 'idea' ? 'Idea sent to the product team, with the page you were on and the app version. Thank you.' : 'Sent to the product team, with the page you were on and the app version. Thank you.');
    setF({ kind: 'idea', title: '', body: '' }); // the button is Suggest idea: the next opening starts on an idea again
    onClose();
  };
  return (
    <Dialog open={open} onClose={onClose} title={f.kind === 'idea' ? 'Suggest an idea' : 'Send feedback'} description="It goes to the Room Map Pro product team. We add the page you’re on, the app version and your browser automatically."
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} busyLabel="Sending…" disabled={!f.title.trim()} onClick={() => void send()}>Send</Button></>}>
      <div className="flex flex-col gap-3">
        <SelectField label="About" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as typeof f.kind })}>
          {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
        </SelectField>
        <TextField label={kind.title} maxLength={200} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <TextArea label="Details" optional maxLength={4000} rows={4} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
        <p className="text-[12px] text-muted" data-testid="feedback-context">Included: {context.path} · version {context.version}</p>
      </div>
    </Dialog>
  );
}
